import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import ApiService from "../../api/apiService";
import { convertTo12HourPlusDateFormat } from "../../util/helperFunctions";
import "../../styles/ccar-detail.css";
import ImagePreviewModal from "./reusable/image-preview-modal";
import showSwal from "../swal.jsx";
import { common } from "../../util/common.js";

const CCAR_LIST_URL = "/requests?p=view-ccar";
const DESCRIPTION_PREVIEW_LENGTH = 420;

const resolveReturnUrl = (value) => {
  if (value == null) return "";

  const trimmed = String(value).trim();
  const normalized = trimmed.toLowerCase();

  if (!trimmed || normalized === "null" || normalized === "undefined") {
    return "";
  }

  return trimmed;
};

const isTruthyValue = (value) =>
  value === true ||
  value === 1 ||
  value === "1" ||
  String(value).toLowerCase() === "true";

const formatDate = (value) =>
  value ? convertTo12HourPlusDateFormat(value) : "—";

const getInitials = (name = "") =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("") || "?";

const getApproverName = (approver) => {
  if (approver?.display_name) return approver.display_name;

  const fullName = [
    approver?.first_name,
    approver?.middle_name,
    approver?.last_name,
  ]
    .filter(Boolean)
    .join(" ")
    .trim();

  return fullName || "Unknown approver";
};

const getFileName = (url) =>
  url?.toString().split("/").pop()?.split("?")?.[0] || "attachment";

const getFileExtension = (fileName = "") => {
  const parts = fileName.split(".");
  return parts.length < 2 ? "FILE" : parts[parts.length - 1].toUpperCase();
};

const parseAttachments = (raw) => {
  if (!raw) return [];

  try {
    let attachments = JSON.parse(raw);

    // Handle double-stringified JSON.
    if (typeof attachments === "string") {
      attachments = JSON.parse(attachments);
    }

    if (!Array.isArray(attachments)) {
      attachments = [attachments];
    }

    return attachments.filter(Boolean);
  } catch (error) {
    return [raw].filter(Boolean);
  }
};

const STATUS_META = {
  Approved: { tone: "approved", icon: "fa-check-circle" },
  Denied: { tone: "denied", icon: "fa-times-circle" },
  Cancelled: { tone: "cancelled", icon: "fa-ban" },
  Pending: { tone: "pending", icon: "fa-clock-o" },
};

function CCARRequestDetail({ setApproversData, userDetails }) {
  const location = useLocation();

  const [ccarData, setCCARData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [returnUrl, setReturnUrl] = useState("");
  const [previewImage, setPreviewImage] = useState(null);
  const [copiedReference, setCopiedReference] = useState(false);
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [approvalRemarks, setApprovalRemarks] = useState("");
  const [approvalAttachment, setApprovalAttachment] = useState(null);
  const [approvalStatus, setApprovalStatus] = useState(null);
  const [isApprovalSubmitting, setIsApprovalSubmitting] = useState(false);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const approvalFileInputRef = useRef(null);

  useEffect(() => {
    const getCCARRequest = async () => {
      try {
        setLoading(true);

        const searchParams = new URLSearchParams(location.search);
        const requestType = searchParams.get("t");
        const requestLinkId = searchParams.get("r");

        setReturnUrl(resolveReturnUrl(searchParams.get("r_url")));

        if (!requestType || !requestLinkId) {
          setCCARData(null);
          return;
        }

        const response = await ApiService.forms.getFormRequestDetail(
          requestType,
          requestLinkId,
        );

        if (!response?.success || !response?.request) {
          setCCARData(null);
          return;
        }

        const request = {
          ...response.request,
          prospecting_images: response.request?.prospecting_images ?? "",
        };

        setCCARData(request);

        if (setApproversData) {
          setApproversData(request?.approval);
        }
      } catch (error) {
        console.error("Failed to retrieve CCAR request:", error);
        setCCARData(null);
      } finally {
        setLoading(false);
      }
    };

    getCCARRequest();
  }, [location.search, setApproversData]);

  // Close the decision dialog with Escape.
  useEffect(() => {
    if (!showApprovalModal) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape" && !isApprovalSubmitting) {
        setShowApprovalModal(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [showApprovalModal, isApprovalSubmitting]);

  const status = ccarData?.final_status || "Pending";
  const statusMeta = STATUS_META[status] || STATUS_META.Pending;
  const isPending = status === "Pending";

  // Every approver once, tagged with the level they sit on.
  const approvers = useMemo(() => {
    const approval = ccarData?.approval || {};
    const tagged = [
      ...(approval.first_level_approval || []).map((approver) => ({
        ...approver,
        level: "Initial",
      })),
      ...(approval.second_level_approval || []).map((approver) => ({
        ...approver,
        level: "Executive",
      })),
    ];

    const byId = new Map();
    for (const approver of tagged) {
      const existing = byId.get(approver.id);
      if (existing) {
        existing.level = `${existing.level} · ${approver.level}`;
      } else {
        byId.set(approver.id, { ...approver });
      }
    }

    return [...byId.values()];
  }, [ccarData]);

  // CCAR is decided by a single approver: whoever voted first.
  const decision = useMemo(
    () =>
      approvers
        .filter((approver) => isTruthyValue(approver?.has_voted))
        .sort((a, b) => new Date(a.date) - new Date(b.date))[0] || null,
    [approvers],
  );

  const canDecide = useMemo(() => {
    if (!isPending || !userDetails?.id) return false;

    const me = approvers.find((approver) => approver?.id === userDetails.id);
    return Boolean(me && !isTruthyValue(me.has_voted));
  }, [approvers, isPending, userDetails]);

  const attachments = parseAttachments(ccarData?.prospecting_images);
  const imageAttachments = attachments.filter((url) =>
    /\.(png|jpe?g|gif|webp|bmp)$/i.test(url.split("?")[0]),
  );
  const fileAttachments = attachments.filter(
    (url) => !imageAttachments.includes(url),
  );

  const description = ccarData?.nc_description?.trim() || "";
  const isDescriptionLong = description.length > DESCRIPTION_PREVIEW_LENGTH;

  const leaveRequestDetail = () => {
    if (returnUrl) {
      window.location.href = returnUrl;
      return;
    }

    if (window.history.length > 1) {
      window.history.back();
      return;
    }

    window.location.href = CCAR_LIST_URL;
  };

  const handleGoBack = (event) => {
    if (returnUrl) return;

    event.preventDefault();
    leaveRequestDetail();
  };

  const handleCopyReference = () => {
    const reference = ccarData?.request_reference_id;
    if (!reference) return;

    navigator.clipboard.writeText(reference);
    setCopiedReference(true);
    setTimeout(() => setCopiedReference(false), 1800);
  };

  const openDecision = (approve) => {
    setApprovalStatus(approve);
    setApprovalRemarks("");
    setApprovalAttachment(null);
    setShowApprovalModal(true);
  };

  const closeDecision = () => {
    if (isApprovalSubmitting) return;

    setShowApprovalModal(false);
    setApprovalRemarks("");
    setApprovalAttachment(null);
  };

  const clearAttachment = (event) => {
    event.stopPropagation();
    setApprovalAttachment(null);

    if (approvalFileInputRef.current) {
      approvalFileInputRef.current.value = "";
    }
  };

  const submitApproval = async () => {
    try {
      setIsApprovalSubmitting(true);

      const formRequestId = ccarData?.form_request_id;

      if (!formRequestId) {
        console.error("Form request id is missing:", ccarData);
        showSwal("Form request ID is missing.");
        return;
      }

      const formData = new FormData();
      formData.append("form_id", ccarData?.form_id || "");
      formData.append("request_id", formRequestId);
      formData.append("status", approvalStatus ? "true" : "false");
      formData.append("remarks", approvalRemarks.trim());

      if (approvalAttachment) {
        formData.append("attachment", approvalAttachment);
      }

      const apiResponse = await ApiService.forms.approveDenyRequest(formData);

      if (!apiResponse?.success) {
        showSwal(apiResponse?.message || common.errors.APPROVE_REQUEST_ERROR);
        return;
      }

      setShowApprovalModal(false);
      setApprovalRemarks("");
      setApprovalAttachment(null);
      setApprovalStatus(null);

      leaveRequestDetail();
    } catch (error) {
      console.error("Failed to submit CCAR decision:", error);
      showSwal(common.errors.APPROVE_REQUEST_ERROR);
    } finally {
      setIsApprovalSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="ccd ccd--center" role="status">
        <span className="ccd-spinner" aria-hidden="true" />
        <span className="u-visually-hidden">Loading CCAR…</span>
      </div>
    );
  }

  if (!ccarData) {
    return (
      <div className="ccd ccd--center">
        <div className="ccd-empty">
          <i className="fa fa-file-text-o" aria-hidden="true" />
          <h2>CCAR not found</h2>
          <p>It may have been removed, or the link is incomplete.</p>
          <a className="ccd-btn ccd-btn--ghost" href={CCAR_LIST_URL}>
            Back to CCAR history
          </a>
        </div>
      </div>
    );
  }

  const facts = [
    { label: "Warehouse", value: ccarData.warehouse, icon: "fa-building-o" },
    { label: "NC source", value: ccarData.nc_source, icon: "fa-flag-o" },
    { label: "Issued to", value: ccarData.issued_to, icon: "fa-users" },
    {
      label: "Submitted",
      value: formatDate(ccarData.createdAt),
      icon: "fa-calendar-o",
    },
  ];

  const details = [
    { label: "Customer / audit ref.", value: ccarData.reference_number },
    { label: "Contact person", value: ccarData.contact_person },
    {
      label: "Contact number",
      value: ccarData.contact_number,
      href: ccarData.contact_number && `tel:${ccarData.contact_number}`,
    },
    {
      label: "Email",
      value: ccarData.email_address,
      href: ccarData.email_address && `mailto:${ccarData.email_address}`,
    },
  ];

  const requesterName = ccarData.user_detail?.display_name;

  return (
    <div className={`ccd${canDecide ? " ccd--has-actions" : ""}`}>
      <div className="ccd-shell">
        <a
          className="ccd-back"
          href={returnUrl || CCAR_LIST_URL}
          onClick={handleGoBack}
        >
          <i className="fa fa-arrow-left" aria-hidden="true" />
          Back
        </a>

        {/* Header */}
        <header className="ccd-card ccd-hero">
          <div className="ccd-hero__top">
            <div className="ccd-hero__heading">
              <span className="ccd-eyebrow">
                Customer Corrective Action Request
              </span>

              <h1 className="ccd-hero__title">
                {ccarData.request_reference_id || "CCAR"}

                {ccarData.request_reference_id && (
                  <button
                    type="button"
                    className="ccd-icon-btn"
                    onClick={handleCopyReference}
                    aria-label="Copy reference number"
                    title={copiedReference ? "Copied" : "Copy reference"}
                  >
                    <i
                      className={`fa ${
                        copiedReference ? "fa-check" : "fa-clone"
                      }`}
                      aria-hidden="true"
                    />
                  </button>
                )}
              </h1>

              {requesterName && (
                <p className="ccd-hero__by">
                  Submitted by <strong>{requesterName}</strong>
                </p>
              )}
            </div>

            <span className={`ccd-status ccd-status--${statusMeta.tone}`}>
              <i className={`fa ${statusMeta.icon}`} aria-hidden="true" />
              {status}
            </span>
          </div>

          <dl className="ccd-facts">
            {facts.map((fact) => (
              <div className="ccd-fact" key={fact.label}>
                <dt>
                  <i className={`fa ${fact.icon}`} aria-hidden="true" />
                  {fact.label}
                </dt>
                <dd>{fact.value || "—"}</dd>
              </div>
            ))}
          </dl>
        </header>

        {/* Decision */}
        {decision ? (
          <section
            className={`ccd-decision ccd-decision--${
              isTruthyValue(decision.approved) ? "approved" : "denied"
            }`}
            aria-label="Decision"
          >
            <div className="ccd-avatar ccd-avatar--lg" aria-hidden="true">
              {getInitials(getApproverName(decision))}
            </div>

            <div className="ccd-decision__body">
              <p className="ccd-decision__title">
                {isTruthyValue(decision.approved) ? "Approved" : "Denied"} by{" "}
                <strong>{getApproverName(decision)}</strong>
              </p>
              <p className="ccd-decision__meta">
                {decision.level} approver · {formatDate(decision.date)}
              </p>

              {decision.remarks && (
                <blockquote className="ccd-decision__remarks">
                  {decision.remarks}
                </blockquote>
              )}

              {decision.attachment && (
                <div className="ccd-decision__file">
                  <i className="fa fa-paperclip" aria-hidden="true" />
                  <span>{getFileName(decision.attachment)}</span>
                  <button
                    type="button"
                    className="ccd-link"
                    onClick={() => setPreviewImage(decision.attachment)}
                  >
                    Preview
                  </button>
                  <a
                    className="ccd-link"
                    href={decision.attachment}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Open
                  </a>
                </div>
              )}
            </div>
          </section>
        ) : (
          isPending && (
            <section
              className="ccd-decision ccd-decision--pending"
              aria-label="Decision"
            >
              <div className="ccd-decision__icon" aria-hidden="true">
                <i className="fa fa-hourglass-half" />
              </div>
              <div className="ccd-decision__body">
                <p className="ccd-decision__title">Awaiting a decision</p>
                <p className="ccd-decision__meta">
                  {canDecide
                    ? "You can approve or deny this CCAR. One decision from any approver closes it."
                    : "Any one of the approvers can approve or deny this CCAR."}
                </p>
              </div>
            </section>
          )
        )}

        <div className="ccd-layout">
          {/* Main column */}
          <div className="ccd-main">
            <section className="ccd-card" aria-labelledby="ccd-nc-title">
              <div className="ccd-card__head">
                <h2 id="ccd-nc-title">Remarks</h2>
                <span className="ccd-card__hint">
                  Pagsalaysay sa Paltos (NC)
                </span>
              </div>

              {description ? (
                <>
                  <p
                    className={`ccd-prose${
                      isDescriptionLong && !showFullDescription
                        ? " is-clamped"
                        : ""
                    }`}
                  >
                    {description}
                  </p>

                  {isDescriptionLong && (
                    <button
                      type="button"
                      className="ccd-link"
                      onClick={() => setShowFullDescription((prev) => !prev)}
                      aria-expanded={showFullDescription}
                    >
                      {showFullDescription ? "Show less" : "Read more"}
                    </button>
                  )}
                </>
              ) : (
                <p className="ccd-muted">No description was provided.</p>
              )}
            </section>

            <section className="ccd-card" aria-labelledby="ccd-files-title">
              <div className="ccd-card__head">
                <h2 id="ccd-files-title">Attachments</h2>
                <span className="ccd-count">{attachments.length}</span>
              </div>

              {attachments.length === 0 && (
                <p className="ccd-muted">No files were attached.</p>
              )}

              {imageAttachments.length > 0 && (
                <ul className="ccd-gallery">
                  {imageAttachments.map((url, index) => (
                    <li key={`${url}-${index}`}>
                      <button
                        type="button"
                        className="ccd-thumb"
                        onClick={() => setPreviewImage(url)}
                        aria-label={`Preview ${getFileName(url)}`}
                      >
                        <img src={url} alt="" loading="lazy" />
                        <span className="ccd-thumb__zoom" aria-hidden="true">
                          <i className="fa fa-search-plus" />
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}

              {fileAttachments.length > 0 && (
                <ul className="ccd-files">
                  {fileAttachments.map((url, index) => {
                    const fileName = getFileName(url);
                    const isPdf = /\.pdf$/i.test(fileName);

                    return (
                      <li className="ccd-file" key={`${url}-${index}`}>
                        <span className="ccd-file__badge">
                          {getFileExtension(fileName)}
                        </span>
                        <span className="ccd-file__name" title={fileName}>
                          {fileName}
                        </span>
                        <span className="ccd-file__actions">
                          {isPdf && (
                            <button
                              type="button"
                              className="ccd-link"
                              onClick={() => setPreviewImage(url)}
                            >
                              View
                            </button>
                          )}
                          <a
                            className="ccd-link"
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Download
                          </a>
                        </span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>
          </div>

          {/* Side column */}
          <aside className="ccd-side">
            <section className="ccd-card" aria-labelledby="ccd-details-title">
              <div className="ccd-card__head">
                <h2 id="ccd-details-title">Reference &amp; contact</h2>
              </div>

              <dl className="ccd-details">
                {details.map((item) => (
                  <div key={item.label}>
                    <dt>{item.label}</dt>
                    <dd>
                      {item.value ? (
                        item.href ? (
                          <a href={item.href}>{item.value}</a>
                        ) : (
                          item.value
                        )
                      ) : (
                        <span className="ccd-muted">Not provided</span>
                      )}
                    </dd>
                  </div>
                ))}
              </dl>
            </section>

            <section
              className="ccd-card"
              aria-labelledby="ccd-approvers-title"
            >
              <div className="ccd-card__head">
                <h2 id="ccd-approvers-title">Approvers</h2>
                <span className="ccd-card__hint">One decision needed</span>
              </div>

              {approvers.length > 0 ? (
                <ul className="ccd-people">
                  {approvers.map((approver) => {
                    const voted = isTruthyValue(approver.has_voted);
                    const approved = voted && isTruthyValue(approver.approved);
                    const isMe = approver.id === userDetails?.id;

                    return (
                      <li className="ccd-person" key={approver.id}>
                        <span className="ccd-avatar" aria-hidden="true">
                          {getInitials(getApproverName(approver))}
                        </span>
                        <span className="ccd-person__text">
                          <span className="ccd-person__name">
                            {getApproverName(approver)}
                            {isMe && <span className="ccd-you">You</span>}
                          </span>
                          <span className="ccd-person__role">
                            {approver.level}
                          </span>
                        </span>
                        {voted && (
                          <span
                            className={`ccd-chip ccd-chip--${
                              approved ? "approved" : "denied"
                            }`}
                          >
                            {approved ? "Approved" : "Denied"}
                          </span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <p className="ccd-muted">No approvers are set for this form.</p>
              )}
            </section>
          </aside>
        </div>
      </div>

      {/* Approver actions */}
      {canDecide && (
        <div className="ccd-actionbar" role="region" aria-label="CCAR actions">
          <p className="ccd-actionbar__text">
            Your decision closes this CCAR for all approvers.
          </p>
          <div className="ccd-actionbar__buttons">
            <button
              type="button"
              className="ccd-btn ccd-btn--danger-ghost"
              onClick={() => openDecision(false)}
            >
              <i className="fa fa-times" aria-hidden="true" />
              Deny
            </button>
            <button
              type="button"
              className="ccd-btn ccd-btn--primary"
              onClick={() => openDecision(true)}
            >
              <i className="fa fa-check" aria-hidden="true" />
              Approve
            </button>
          </div>
        </div>
      )}

      {/* Decision dialog */}
      {showApprovalModal && (
        <div className="ccd-overlay" onMouseDown={closeDecision}>
          <div
            className="ccd-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="ccd-dialog-title"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className="ccd-dialog__head">
              <span
                className={`ccd-dialog__icon ccd-dialog__icon--${
                  approvalStatus ? "approved" : "denied"
                }`}
                aria-hidden="true"
              >
                <i className={`fa ${approvalStatus ? "fa-check" : "fa-times"}`} />
              </span>
              <div>
                <h3 id="ccd-dialog-title">
                  {approvalStatus ? "Approve this CCAR?" : "Deny this CCAR?"}
                </h3>
                <p>
                  This is final and closes the CCAR for every approver.
                </p>
              </div>
              <button
                type="button"
                className="ccd-icon-btn"
                onClick={closeDecision}
                aria-label="Close"
              >
                <i className="fa fa-times" aria-hidden="true" />
              </button>
            </div>

            <div className="ccd-dialog__body">
              <label className="ccd-label" htmlFor="ccd-remarks">
                Remarks <span className="ccd-optional">Optional</span>
              </label>
              <textarea
                id="ccd-remarks"
                className="ccd-textarea"
                value={approvalRemarks}
                placeholder={
                  approvalStatus
                    ? "Add a note for the requester"
                    : "Let the requester know why"
                }
                rows={4}
                autoFocus
                onChange={(event) => setApprovalRemarks(event.target.value)}
              />

              <span className="ccd-label" id="ccd-upload-label">
                Supporting document{" "}
                <span className="ccd-optional">Optional</span>
              </span>

              <input
                ref={approvalFileInputRef}
                id="ccd-upload"
                className="ccd-file-input"
                type="file"
                aria-labelledby="ccd-upload-label"
                onChange={(event) =>
                  setApprovalAttachment(event.target.files?.[0] || null)
                }
              />

              {approvalAttachment ? (
                <div className="ccd-picked">
                  <i className="fa fa-file-o" aria-hidden="true" />
                  <span>{approvalAttachment.name}</span>
                  <button
                    type="button"
                    className="ccd-icon-btn"
                    onClick={clearAttachment}
                    aria-label={`Remove ${approvalAttachment.name}`}
                  >
                    <i className="fa fa-times" aria-hidden="true" />
                  </button>
                </div>
              ) : (
                <label className="ccd-drop" htmlFor="ccd-upload">
                  <i className="fa fa-cloud-upload" aria-hidden="true" />
                  <span>
                    <strong>Choose a file</strong> to attach
                  </span>
                </label>
              )}
            </div>

            <div className="ccd-dialog__foot">
              <button
                type="button"
                className="ccd-btn ccd-btn--ghost"
                onClick={closeDecision}
                disabled={isApprovalSubmitting}
              >
                Cancel
              </button>
              <button
                type="button"
                className={`ccd-btn ${
                  approvalStatus ? "ccd-btn--primary" : "ccd-btn--danger"
                }`}
                disabled={isApprovalSubmitting}
                onClick={submitApproval}
              >
                {isApprovalSubmitting
                  ? "Submitting…"
                  : approvalStatus
                    ? "Approve CCAR"
                    : "Deny CCAR"}
              </button>
            </div>
          </div>
        </div>
      )}

      <ImagePreviewModal
        image={previewImage}
        onClose={() => setPreviewImage(null)}
      />
    </div>
  );
}

export default CCARRequestDetail;
